import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ScrollText, ReceiptText, Users as UsersIcon, Sandwich, ShoppingBasket,
  Menu, X, LogOut, Wand2, ShieldCheck, Wallet, History, Boxes, ChefHat,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";

// Each NAV item declares which roles can see it.
const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true,
    roles: ["admin", "encargado"] },
  { to: "/admin/pedidos", label: "Pedidos", icon: ScrollText, testid: "nav-pedidos",
    roles: ["admin", "encargado"] },
  { to: "/admin/remitos", label: "Remitos", icon: ReceiptText, testid: "nav-remitos",
    roles: ["admin", "encargado"] },
  { to: "/admin/produccion", label: "Producción", icon: ChefHat, testid: "nav-produccion",
    roles: ["admin", "encargado"] },
  { to: "/admin/stock", label: "Stock", icon: Boxes, testid: "nav-stock",
    roles: ["admin", "encargado"] },
  { to: "/admin/cuenta-corriente", label: "Cta. Corriente", icon: Wallet, testid: "nav-cuenta-corriente",
    roles: ["admin", "encargado"] },
  { to: "/admin/clientes", label: "Usuarios", icon: UsersIcon, testid: "nav-clientes",
    roles: ["admin"] },
  { to: "/admin/productos", label: "Productos", icon: Sandwich, testid: "nav-productos",
    roles: ["admin", "encargado"] },
  { to: "/admin/insumos", label: "Insumos", icon: ShoppingBasket, testid: "nav-insumos",
    roles: ["admin", "encargado"] },
  { to: "/admin/auditoria", label: "Auditoría", icon: History, testid: "nav-auditoria",
    roles: ["admin"] },
];

const SECONDARY = [
  { to: "/admin/carga-rapida", label: "Carga rápida", icon: Wand2, testid: "nav-carga-rapida",
    roles: ["admin", "encargado"] },
  { to: "/admin/cuenta", label: "Mi cuenta", icon: ShieldCheck, testid: "nav-cuenta",
    roles: ["admin", "encargado"] },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "admin";
  const visibleNav = NAV.filter((it) => it.roles.includes(role));
  const visibleSecondary = SECONDARY.filter((it) => it.roles.includes(role));
  const roleLabel = role === "encargado" ? "Encargado" : "Admin";
  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Brand: never scrolls */}
      <Link
        to="/admin" onClick={onNavigate}
        className="flex items-center gap-3 px-2 py-2 mb-4 group shrink-0"
        data-testid="brand-link"
      >
        <div className="h-14 w-14 rounded-xl bg-white p-1.5 shadow-md transition-transform group-hover:scale-105 shrink-0">
          <img src="/logo.png" alt="Sandwichs Lo De Dani" className="h-full w-full object-contain" />
        </div>
        <div className="leading-tight min-w-0">
          <div className="font-display font-bold text-[15px] text-[#F9F6F0] tracking-tight truncate">Sandwichs Lo De Dani</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#D49A36] font-medium">{roleLabel}</div>
        </div>
      </Link>

      {/* Scrollable area: nav + tools */}
      <nav
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1 -mr-1 flex flex-col gap-1"
        style={{ WebkitOverflowScrolling: "touch" }}
        data-testid="sidebar-scroll-area"
      >
        {visibleNav.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to} to={to} end={end} onClick={onNavigate} data-testid={testid}
            className={({ isActive }) =>
              `nav-link relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium hover:bg-white/5 ${
                isActive ? "text-[#F9F6F0] bg-white/[0.08]" : "text-[#C9C2B4] hover:text-[#F9F6F0]"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-[#D49A36]" />}
                <Icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2.4 : 2} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.18em] text-[#8C8474] font-semibold">Herramientas</div>
          {visibleSecondary.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to} to={to} onClick={onNavigate} data-testid={testid}
              className={({ isActive }) =>
                `nav-link relative flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium hover:bg-white/5 ${
                  isActive ? "text-[#F9F6F0] bg-white/[0.08]" : "text-[#A39A87] hover:text-[#F9F6F0]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Footer: user + logout — pinned, always visible */}
      <div
        className="shrink-0 mt-4 pt-4 border-t border-white/10"
        style={{ paddingBottom: "calc(8px + env(safe-area-inset-bottom))" }}
      >
        <div className="px-3 mb-3">
          <div className="text-[12px] text-[#F9F6F0] font-semibold truncate">{user?.nombre || "Admin"}</div>
          <div className="text-[11px] text-[#A39A87] truncate">{user?.email}</div>
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-[14px] text-[#F9F6F0] bg-white/10 hover:bg-white/20 rounded-md font-semibold"
          data-testid="admin-logout"
        >
          <LogOut className="h-4 w-4" /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile sidebar is open (prevents the page behind from scrolling).
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <div className="min-h-screen bg-[#F9F6F0] text-[#1A1A1A]">
      <aside
        className="hidden lg:flex fixed left-0 top-0 h-screen w-64 bg-[#2C2A26] text-[#F9F6F0] px-5 py-6 flex-col z-30"
        data-testid="sidebar-desktop"
      >
        <SidebarContent />
      </aside>

      <header
        className="lg:hidden sticky top-0 z-30 bg-[#2C2A26] text-[#F9F6F0] px-4 py-3 flex items-center justify-between"
        data-testid="topbar-mobile"
      >
        <Link to="/admin" className="flex items-center gap-2.5" data-testid="brand-mobile">
          <div className="h-11 w-11 rounded-lg bg-white p-1 shrink-0">
            <img src="/logo.png" alt="" className="h-full w-full object-contain" />
          </div>
          <div className="font-display font-bold text-[14px]">Lo De Dani · {(user?.role === "encargado" ? "Encargado" : "Admin")}</div>
        </Link>
        <button onClick={() => setOpen((v) => !v)} aria-label="Menú" className="p-2 rounded-md hover:bg-white/10" data-testid="menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </header>

      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="mobile-overlay"
        >
          <aside
            className="absolute top-0 left-0 w-72 max-w-[85vw] bg-[#2C2A26] text-[#F9F6F0] px-5 py-5 flex flex-col"
            style={{ height: "100dvh" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="sidebar-mobile"
          >
            <SidebarContent onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <main className="lg:ml-64 min-h-screen">
        <div className="px-4 py-6 sm:px-6 lg:px-10 lg:py-10 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
